// ============================================================================
// layer.batches.detail — the layer batch record page
// ============================================================================
// Five values used to arrive here as LayerEntry props off App's boot: the batch
// and housing directories, the ppp-layer-groups-v1 blob, the ppp-v4 broiler
// batches, and feedCosts. The first four are read by the surface itself now;
// the fifth was dead. On top of that the page paginated layer_dailys and
// egg_dailys for itself through a `fetchAll(table, columns)` helper whose table
// was a PARAMETER, so both reads inventoried as `from:~table` and could prove
// ownership of neither literal table. All six now belong to two resources:
//
//   layer.record.core       both directories plus the two metric feeds, each
//                           read written out per table so it is attributable.
//   layer.record.appStore   the two persisted blobs, in one keyed read.
//
// Deliberately NOT layer.batches.core, whose four reads are identical.  That
// resource is pinned to exactly one surface — the boot-released /layer/batches
// hub — and layer_batches_boot_release_static asserts the pinning. Widening it
// to save a query definition would loosen a released surface's ownership.
//
// PERMANENT since the boot release. Its housing sibling reached this state one
// wave earlier and for one of these two reasons; the paragraphs below say what
// the residuals WERE and why removing them had to land with the release itself.
//
// ── WHAT THE RESIDUALS WERE, AND WHAT THEY WERE NOT ─────────────────────────
//
// Neither was the database write. This page performs THREE writes and every one
// of them was already page-local, and none of them moved:
//
//   persistBatchRec     `sb.from('layer_batches').upsert(rec, {onConflict})`
//   handleDeleteBatch   `deleteLayerBatch(sb, batch.id)` — the SECDEF RPC from
//                       mig 106, which clears child housings, deletes the batch
//                       root and writes the audit event in one transaction
//   saveNewHousing      `sb.from('layer_housings').upsert(rec, {onConflict})`
//
// (Since migration 242 (R4) the two upserts are gone: persistBatchRec,
// persistRetirementHomeDetails and saveNewHousing each call
// layer_apply_operation through src/layer/layerRecordOps.ts. That changed the
// writes, not this surface's data ownership described here.)
//
// What was App's is the POST-write fan-out, in two different shapes:
//
//   legacy.context.layer.batches    LayerContext's RAW `setLayerBatches`. A bare
//                                   `useState` setter reached through the
//                                   context; it performs no write and fires no
//                                   maintenance.
//   legacy.context.layer.housings   App's `persistLayerHousings`, which was only
//                                   `setLayerHousings(nb)` plus
//                                   `triggerMaintenance({layerHousings: nb, …})`
//                                   and had no `sbSave` either — unlike its
//                                   `persistLayerGroups` sibling, which does own
//                                   a real app_store write and is why
//                                   `view.layers` is still PARTIAL.
//
// So neither residual moved through the route mutation catalog, and a lane that
// read "the write path is still App's" as "catalog the writes" could not have
// shrunk either. A catalog entry's `invalidates` set reaches route RESOURCES
// only. The things these fan-outs fed are neither:
//
//   LayerContext.layerBatches    Historical App state formerly populated by
//   LayerContext.layerHousings   `loadLayerBootData`. The released readers now own
//                                their resources directly; the setters remain
//                                local compatibility APIs, not data owners.
//   triggerMaintenance           Historical App-local fan-out. It is no longer
//                                a route-transition fallback or a source of
//                                global readiness.
//
// ── WHAT RETIRED THEM ───────────────────────────────────────────────────────
//
// The release landed atomically with dropping both props — not as a partial
// migration of the old readers. The all-data loader and its App-local fan-out
// are now retired; this record surface owns and revalidates its resources, and
// a raw pathname outside its strict contract receives explicit recovery rather
// than repopulating LayerContext. The former fan-outs were load-bearing before
// that boundary existed, which is why the halves could not have been split
// across their original change.
//
// `persistLayerHousings` had no caller left after this and was deleted from
// App.tsx outright. `setLayerBatches` remains a local compatibility setter,
// not a target for the retired loader.
//
// ── WHY A PARTIAL CATALOG CONVERSION WAS NOT A SMALLER FIRST STEP ───────────
//
// The same reason its housing sibling gives. Cataloguing just the discrete
// writes and leaving the debounced `scheduleBatchAutosave` inline does not
// typecheck as a claim: the surface would hold one resource whose invalidation
// set names tables a live inline writer still mutates behind its back.
// Converting the autosave too is not free either — `useRouteMutation` awaits its
// invalidations, so every autosave tick would block on a `layer.record.core`
// refetch of four tables including the paginated daily feeds, where today it
// updates a local overlay. That is still true, and still a separate decision:
// this release did not convert any of the three writes.
// ============================================================================

import type {MigratedSurfaceDeclaration} from '../surfaceDataManifest';

const declaration: MigratedSurfaceDeclaration = Object.freeze({
  surface: 'layer.batches.detail',
  obligations: Object.freeze([
    Object.freeze({id: 'layer.record.core', owner: 'permanent'}),
    Object.freeze({id: 'layer.record.appStore', owner: 'permanent'}),
  ]),
  residualLegacy: Object.freeze([] as const),
});

export default declaration;
